import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_DESCRIPTION,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_REPAY,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_REPAY_BUTTON,
	PAY_ALL_INVOICES,
	INVOICES_TITLE_SUCCESS,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_CONDITIONAL_DESCRIPTION,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_TITLE,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_PAY_TEXT,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_HEADER_INVOICES,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_HEADER_INVOICE,
	ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_TOTAL_AMOUNT,
} from '../../../../constants/Copy';
import { PAYMENT_ORIGIN } from '../../../../constants/Payment';
import SubscriptionCancelledInvoiceListItem from '../../../account/subscription-cancelled-invoices-list/SubscriptionCancelledInvoiceListItem';
import Heading, { HEADING_SIZE } from '../../../common/heading/Heading';
import { hasValidVideolandCookie } from '../../../../helpers/authentication';
import { ROUTE_LOGIN, ROUTE_PAY_BAD_DEBT } from '../../../../constants/Routes';
import { useInvoices } from '../../../../hooks/useInvoices';
import { checkRouteWinbackAndRedirect } from '../../../../helpers/redirect';
import { formatPrice } from '../../../../helpers/number';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router';
import Button, {
	BUTTON_KIND_ACTION,
	BUTTON_KIND_CANCEL,
} from '../../../common/buttons/button/Button';
import { analyticsPush } from '../../../../actions/applicationActions';
import { EVT_PAY_OPEN_INVOICE_CLICKED_PAY } from '../../../../constants/EventTypes';
import SubscriptionCancelledInvoiceListItemLoader from '../../../account/subscription-cancelled-invoices-list/SubscriptionCancelledInvoiceListItemLoader';
import { initiateTrackStructEvent } from '../../../analytics/Snowplow';
import {
	EVT_BAD_DEBT,
	EVT_ALL_INVOICES,
	EVT_INVOICE_CONFIRM,
	EVT_INVOICE_SELECT,
	EVT_SHOW,
	EVT_INVOICE_CONFIRMATION,
	EVT_SECTION,
	EVT_CONFIRMATION,
} from '../../../../constants/SnowplowEvents';
import { useQuery } from '@apollo/client';
import queryPaymentDetails from '../../../../graphql/queryPaymentDetails.graphql';
import { getPaymentClient } from '../../../../api/paymentService';
import cx from 'classnames';
import { getAccountClient } from '../../../../api/accountService';
import queryBadDebtInvoiceData from '../../../../graphql/queryBadDebtInvoiceData.graphql';

import './SubscriptionCancelledInvoices.scss';

function handlePayAllInvoicesClick({ label, dispatch, history }) {
	initiateTrackStructEvent({
		category: EVT_BAD_DEBT,
		action: EVT_INVOICE_SELECT,
		label: PAY_ALL_INVOICES,
	});
	dispatch(
		analyticsPush(
			{
				label,
				origin: PAYMENT_ORIGIN.SUBSCRIPTION_CANCELLED,
				allInvoices: true,
			},
			EVT_PAY_OPEN_INVOICE_CLICKED_PAY
		)
	);
	history.push(ROUTE_PAY_BAD_DEBT);
}

function RenderLoadingInvoices() {
	return (
		<ul>
			<SubscriptionCancelledInvoiceListItemLoader />
		</ul>
	);
}

function RenderOpenInvoices({ invoicesWithOpenPayments }) {
	return (
		<ul>
			{invoicesWithOpenPayments.map((invoice, index) => (
				<SubscriptionCancelledInvoiceListItem
					invoice={invoice}
					key={invoice.id}
					index={index}
					origin={PAYMENT_ORIGIN.SUBSCRIPTION_CANCELLED}
				/>
			))}
		</ul>
	);
}

function RenderBody({ invoicesWithOpenPayments, loading, invoices, dispatch, history }) {
	const accountClient = getAccountClient();
	const { data } = useQuery(queryBadDebtInvoiceData, {
		client: accountClient,
		fetchPolicy: 'no-cache',
	});

	const totalAmount =
		data?.me.badDebtInvoiceData.amount ??
		invoicesWithOpenPayments.reduce((accumulator, current) => accumulator + current.totalAmount, 0);

	return (
		<div>
			<header>
				<h2 className="profile__heading profile__heading--with-button">
					{invoicesWithOpenPayments.length > 1
						? ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_HEADER_INVOICES
						: ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_HEADER_INVOICE}
				</h2>
			</header>
			{loading ? (
				<RenderLoadingInvoices />
			) : (
				invoices && <RenderOpenInvoices invoicesWithOpenPayments={invoicesWithOpenPayments} />
			)}

			{invoicesWithOpenPayments.length > 1 && (
				<>
					<p className="cancellation-invoices-pay-all__total-amount">
						{ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_TOTAL_AMOUNT} &euro;
						{formatPrice(totalAmount)}
					</p>
					<div className="cancellation-invoices-pay-all">
						<Button
							kind={BUTTON_KIND_ACTION}
							onClick={() =>
								handlePayAllInvoicesClick({ label: PAY_ALL_INVOICES, history, dispatch })
							}
						>
							{PAY_ALL_INVOICES}
						</Button>
						<p className="cancellation-invoices-pay-all__text">
							{ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_PAY_TEXT}
						</p>
					</div>
				</>
			)}
			<p
				className="cancellation-invoices-contact"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_DESCRIPTION[1],
				}}
			/>
		</div>
	);
}

const SubscriptionCancelledInvoices = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const isLoggedIn = hasValidVideolandCookie();
	const router = useSelector((state) => state.router);
	const [showMessageRepay, setShowMessageRepay] = useState(false);
	const { invoices, loading, startPollingInvoices, stopPollingInvoices } = useInvoices();

	const invoicesWithOpenPayments = useMemo(
		() => invoices?.filter((invoice) => invoice.openPayments.length > 0) || [],
		[invoices]
	);

	const paymentClient = getPaymentClient();
	const { data: paymentDetails } = useQuery(queryPaymentDetails, {
		client: paymentClient,
		skip: !isLoggedIn,
	});

	const isMultipleInvoices = invoicesWithOpenPayments.length > 1;

	const invoicesDescription = isMultipleInvoices
		? ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_CONDITIONAL_DESCRIPTION.MULTIPLE
		: ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_CONDITIONAL_DESCRIPTION.ONE;

	useEffect(() => {
		if (!loading && invoices) {
			if (invoicesWithOpenPayments.length > 0) {
				startPollingInvoices(20000);
			} else if (invoicesWithOpenPayments.length === 0) {
				stopPollingInvoices();
				setShowMessageRepay(true);
			}
		}
	}, [
		loading,
		startPollingInvoices,
		stopPollingInvoices,
		invoices,
		router,
		invoicesWithOpenPayments,
	]);

	if (!isLoggedIn) {
		return <Redirect to={ROUTE_LOGIN} />;
	}

	const MessageRepay = () => {
		return (
			<>
				<Heading
					size={HEADING_SIZE.LARGE}
					className="full-page-message__heading"
					testId="full-page-message-title"
				>
					{INVOICES_TITLE_SUCCESS}
				</Heading>
				<div className="onboarding__text" data-test="full-page-message-description">
					<p className="cancellation-invoices-description">
						{ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_REPAY}
					</p>
				</div>
				<Button
					kind={BUTTON_KIND_CANCEL}
					onClick={async () => {
						await initiateTrackStructEvent({
							category: EVT_BAD_DEBT,
							action: EVT_INVOICE_CONFIRM,
							label: EVT_ALL_INVOICES,

							rtlElementContext: {
								elementInteraction: EVT_SHOW,
								elementGroup: EVT_INVOICE_CONFIRMATION,
								elementType: EVT_SECTION,
								elementName: EVT_CONFIRMATION,
							},
						});
						checkRouteWinbackAndRedirect(router);
					}}
				>
					{ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_REPAY_BUTTON}
				</Button>
			</>
		);
	};

	return (
		<section
			className={cx('cancellation-invoices-container', {
				['cancellation-invoices-container-multiple']: invoicesWithOpenPayments.length > 1,
			})}
		>
			{showMessageRepay ? (
				<MessageRepay />
			) : (
				<>
					<Heading
						size={HEADING_SIZE.LARGE}
						className="full-page-message__heading"
						testId="full-page-message-title"
					>
						{ONBOARDING_ERROR_SUBSCRIPTION_ENDED_SUBSCRIPTION_TITLE}
					</Heading>
					<div className="onboarding__text" data-test="full-page-message-description">
						{invoicesDescription.map((text, index) => (
							<p
								className="cancellation-invoices-description"
								key={index}
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: text.replace(
										/\{\{([^}]+)\}\}/,
										paymentDetails?.me?.paymentDetails
											? `<span>${paymentDetails.me.paymentDetails.maskedIban}</span>`
											: ''
									),
								}}
							/>
						))}
					</div>
					<RenderBody
						invoicesWithOpenPayments={invoicesWithOpenPayments}
						loading={loading}
						invoices={invoices}
						dispatch={dispatch}
						history={history}
					/>
				</>
			)}
		</section>
	);
};

export default SubscriptionCancelledInvoices;
